import type { ColumnDef } from '@tanstack/react-table';
import { RBAC_PERMISSIONS } from '@/constants/permissions';
import {
  createActionsColumn,
  createSerialColumn,
  WrappingCellText,
} from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { EmptyValue } from '@/components/common/EmptyValue';
import { HighlightText } from '@/components/common/HighlightText';
import { RowActionGroup, RowEditDeleteButtons } from '@/components/common/RowActions';
import { formatCount } from '@/utils/number';
import { isProtectedRole, type Role } from '../types/role.types';

interface RoleColumnActions {
  onEdit: (role: Role) => void;
  onDelete: (role: Role) => void;
}

export function getRoleColumns(
  searchText?: string,
  actions?: RoleColumnActions,
): ColumnDef<Role, unknown>[] {
  return [
    createSerialColumn<Role>(),
    {
      accessorKey: 'name',
      header: 'Role Name',
      cell: ({ row }) => (
        <span className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </span>
      ),
    },
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => (
        <span className="font-mono text-xs">
          <HighlightText text={row.original.code} highlightedText={searchText} />
        </span>
      ),
    },
    {
      accessorKey: 'description',
      header: 'Details',
      cell: ({ row }) => {
        const description = row.original.description;
        if (!description) return <EmptyValue />;
        return (
          <WrappingCellText width="wide">
            <HighlightText text={description} highlightedText={searchText} />
          </WrappingCellText>
        );
      },
    },
    {
      accessorKey: 'heldPermissionCount',
      header: 'Permissions',
      cell: ({ row }) => (
        <span
          className="tabular-nums"
          title={formatCount(row.original.heldPermissionCount, 'permission')}
        >
          {row.original.heldPermissionCount}
        </span>
      ),
    },
    {
      accessorKey: 'assignedUserCount',
      header: 'Users',
      cell: ({ row }) => (
        <span className="tabular-nums" title={formatCount(row.original.assignedUserCount, 'user')}>
          {row.original.assignedUserCount}
        </span>
      ),
    },
    {
      accessorKey: 'isActive',
      header: 'Status',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    ...(actions
      ? [
          createActionsColumn<Role>((role) => (
            <RowActionGroup>
              <RowEditDeleteButtons
                item={role}
                itemName={role.name}
                onEdit={actions.onEdit}
                onDelete={isProtectedRole(role) ? undefined : actions.onDelete}
                editRequires={RBAC_PERMISSIONS.ROLE_EDIT}
                deleteRequires={RBAC_PERMISSIONS.ROLE_DELETE}
              />
            </RowActionGroup>
          )),
        ]
      : []),
  ];
}
